import { useQuery } from 'react-query'
import { getSpendBreakdown, getSpendSummary, getSpendTimeseries } from './spendApi'
import { SpendBucketSize, SpendFilters, SpendGrouping } from './spendModel'

/**
 * Errors are surfaced in the panel rather than through a notification toast.
 *
 * A spend figure that failed to load must not read as a spend figure of zero,
 * so the panel needs to know the request failed — a toast that scrolls away
 * leaves an empty dashboard looking like a real answer.
 */
export const useSpendSummary = (filters: SpendFilters) => {
  return useQuery({
    queryKey: ['spendSummary', filters],
    queryFn: () => getSpendSummary(filters),
    keepPreviousData: true,
    retry: false,
  })
}

export const useSpendTimeseries = (filters: SpendFilters, bucket: SpendBucketSize) => {
  return useQuery({
    queryKey: ['spendTimeseries', filters, bucket],
    queryFn: () => getSpendTimeseries(filters, bucket),
    keepPreviousData: true,
    retry: false,
  })
}

export const useSpendBreakdown = (
  filters: SpendFilters,
  by: SpendGrouping,
  limit: number,
  { enabled = true, staleTime = 0 }: { enabled?: boolean; staleTime?: number } = {},
) => {
  return useQuery({
    queryKey: ['spendBreakdown', filters, by, limit],
    queryFn: () => getSpendBreakdown(filters, by, limit),
    enabled,
    staleTime,
    keepPreviousData: true,
    retry: false,
  })
}

const PLAN_OPTIONS_STALE_MS = 60_000

/**
 * The plans that have spend in the window, for the plan filter.
 *
 * This is the plan breakdown, through the one hook that owns it, so the filter
 * and a Plan breakdown of the same window share a request and a cache entry.
 * Only fetched once `enabled` (the filter has been opened), so loading the
 * panel never makes a plan request. The filter's own plan is dropped from the
 * window: narrowing the choices to the plan already chosen would leave one.
 */
export const useSpendPlanOptions = (
  filters: SpendFilters,
  limit: number,
  enabled: boolean,
) => {
  const { planName: _chosen, ...window } = filters
  // Opening the filter enables a second observer; without a staleTime that
  // alone would refetch what a Plan breakdown of the same window just loaded.
  return useSpendBreakdown(window, 'plan', limit, { enabled, staleTime: PLAN_OPTIONS_STALE_MS })
}
